import React, { useState, useEffect, useCallback } from "react";
import DownloadModalTables from "../../components/DownloadModalTables";
import DateFilter from "../../components/DateFilter";
import PortalPopup from "../../components/PortalPopup";
import DebtorDeletion from "../../components/DebtorDeletion";
import ImagePreview from "../../components/ImagePreview";
import LBAReminder from "../../components/LBAReminder";
import NavigationDropdown from "../../components/NavigationDropdown";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import { useNavigate } from "react-router-dom";
import NavigationPanelLawyer from '../../components/NavigationPanelLawyer';
import { documentService } from "../../services/documentService";
import CTAs from "../../components/CTAs";
import { useLocation, useParams } from "react-router-dom";
import "../../global.css";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Footer from "../../components/Footer.js";

const FilePreviewModal = ({ fileUrl, fileType, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white p-6 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-2xl font-bold text-gray-600 hover:text-gray-900"
        >
          ×
        </button>

        {fileType === "pdf" ? (
          <iframe
            src={fileUrl}
            width="100%"
            height="600px"
            title="PDF Preview"
          />
        ) : fileType.match(/jpg|jpeg|png|gif/i) ? (
          <img
            src={fileUrl}
            alt="Document Preview"
            className="max-w-full max-h-[600px] object-contain"
          />
        ) : (
          <iframe
            src={fileUrl}
            width="100%"
            height="600px"
            title="Document Preview"
          />
        )}
      </div>
    </div>
  );
};

const ClientDocuments = () => {
  const [isDownloadModalTablesOpen, setDownloadModalTablesOpen] = useState(false);
  const [isDateFilterOpen, setDateFilterOpen] = useState(false);
  const [isDebtorDeletionOpen, setDebtorDeletionOpen] = useState(false);
  const [isImagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [isLBAReminderOpen, setLBAReminderOpen] = useState(false);
  const [isNavigationDropdownOpen, setNavigationDropdownOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [filePreview, setFilePreview] = useState(null);

  const location = useLocation();
  const { lbaId } = useParams();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Get all the data passed from the previous page
  const { 
    name, 
    email, 
    amount, 
    currency = 'GBP', 
    address = {},
    invoiceNumber,
    dueDate,
    status,
    city,
    country,
    postalCode,
    street,
    firmName,
    firmEmail
  } = location.state || {};

  const [documents, setDocuments] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear().toString().slice(-2)}`;
  };

  const formatCurrency = (amount, currencyCode) => {
    if (!amount) return '0';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const getFileIcon = (fileType) => {
    switch (fileType.toLowerCase()) {
      case "pdf":
        return "assets/pdf.svg";
      case "docx":
      case "doc":
        return "assets/word_file.svg";
      default:
        return "assets/file.svg";
    }
  };

  const formatFileSize = (sizeInBytes) => {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes}B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(1)}KB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(1)}MB`;
    }
  };

  // Fixed URL construction using the same pattern as your working code
  const constructFileUrl = (lbaId, fileIndex) => {
    return `${API_URL}/api/official_documents/file/${lbaId}/${fileIndex}`;
  };

  const handlePreview = async (lbaId, fileIndex, fileName) => {
    try {
      const fileUrl = constructFileUrl(lbaId, fileIndex);
      
      if (!fileUrl) {
        throw new Error("Invalid file path");
      }

      console.log("Preview URL:", fileUrl);
      
      // Get file extension
      const fileExtension = fileName.split('.').pop().toLowerCase();
      
      // Categorize file types
      const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
      const pdfType = ['pdf'];
      const textTypes = ['txt', 'csv'];
      const officeTypes = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
      
      if ([...imageTypes, ...pdfType, ...textTypes].includes(fileExtension)) {
        // These can be previewed in browser
        console.log(`Opening .${fileExtension} file in new tab`);
        
        const newWindow = window.open(fileUrl, '_blank');
        
        if (!newWindow) {
          alert('Popup blocked. Please allow popups for this site to preview files.');
          return;
        }
        
        // Optional: Add a fallback if the file doesn't load properly
        setTimeout(() => {
          try {
            if (newWindow.closed) {
              // Window was closed quickly, probably an error
              alert('Could not preview the file. It may be downloading instead.');
            }
          } catch (e) {
            // Cross-origin restriction, can't check window status
          }
        }, 2000);
        
      } else if (officeTypes.includes(fileExtension)) {
        // Office files - try to open in Office Online or download
        alert(`Office documents (.${fileExtension}) are best viewed by downloading and opening in their native applications. Downloading now.`);
        handleDownload(lbaId, fileIndex, fileName);
      } else {
        // Unknown or binary files - download directly
        alert(`This file type (.${fileExtension}) cannot be previewed in the browser. Downloading instead.`);
        handleDownload(lbaId, fileIndex, fileName);
      }
    } catch (error) {
      console.error("Error previewing document:", error);
      alert(`Failed to preview the document: ${error.message}. Downloading instead.`);
      handleDownload(lbaId, fileIndex, fileName);
    }
  };

  const handleDownload = async (lbaId, fileIndex, fileName) => {
    try {
      const fileUrl = constructFileUrl(lbaId, fileIndex);
      
      if (!fileUrl) {
        throw new Error("Invalid file path");
      }

      const filename = fileName || "downloaded-file.pdf";

      console.log("Download URL:", fileUrl);

      // Fetch the file
      const response = await fetch(fileUrl);

      // Check if the response is ok
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob
      const blob = await response.blob();

      // Create a link element
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;

      // Programmatically trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert(`Failed to download the file: ${error.message}`);
    }
  };

  const handleDownloadAll = async () => {
    try {
      if (documents.length === 0 || !lbaId) {
        alert("No documents available for download.");
        return;
      }

      const zip = new JSZip();
      const folder = zip.folder("Documents");

      // Process each document and its files using the same pattern as your working code
      const downloadPromises = documents.flatMap((doc) =>
        doc.files.map(async (file, fileIndex) => {
          try {
            const fileUrl = constructFileUrl(lbaId, fileIndex);
            
            if (!fileUrl) {
              console.error(`Invalid file path for: ${file.fileName}`);
              return;
            }

            const filename = file.fileName || "unknown-file.pdf";

            const response = await fetch(fileUrl);
            if (!response.ok) throw new Error(`Failed to fetch ${filename}`);

            const blob = await response.blob();
            folder.file(filename, blob);
          } catch (error) {
            console.error(`Error downloading ${file.fileName || "file"}:`, error);
          }
        })
      );

      await Promise.all(downloadPromises);
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "Documents.zip");
    } catch (error) {
      console.error("Error downloading ZIP:", error);
      alert("Failed to download files.");
    }
  };

  // Fetch documents
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const data = await documentService.getDocuments(lbaId);
        console.log("Raw documents response:", data);

        let documentsArray = [];

        if (Array.isArray(data)) {
          documentsArray = data;
        } else if (data && Array.isArray(data.documents)) {
          documentsArray = data.documents;
        } else if (data && Array.isArray(data.data)) {
          documentsArray = data.data;
        } else if (data && typeof data === "object") {
          // If it's a single object, wrap it in an array
          documentsArray = [data];
        } else {
          documentsArray = [];
        }

        console.log("Processed documents array:", documentsArray);

        // Transform the data to match your component's expected structure
        const transformedDocuments = documentsArray.map((doc, index) => {
          // If the document already has a files array, use it
          if (doc.files && Array.isArray(doc.files)) {
            return {
              ...doc,
              files: doc.files.map((file, fileIndex) => ({
                ...file,
                index: file.fileIndex || fileIndex, // Use fileIndex from API or fallback to array index
                fileName: file.name || file.fileName || `document-${index}-${fileIndex}`,
                fileSize: file.size || file.fileSize || 0,
              }))
            };
          }
          
          // If it's a single file entry (like in your raw response), create a files array
          return {
            documentId: doc.documentId || doc.id || `doc-${index}`,
            lbaId: doc.lbaId,
            createdAt: doc.createdAt,
            totalFiles: 1,
            files: [
              {
                index: doc.fileIndex || 0,
                fileName: doc.name || doc.fileName || `document-${index}`,
                fileSize: doc.size || doc.fileSize || 0,
                mimeType: doc.mimeType,
                // Include any other file properties that might be useful
                ...doc
              }
            ]
          };
        });

        console.log("Transformed documents:", transformedDocuments);
        setDocuments(transformedDocuments);
      } catch (error) {
        console.error("Error fetching documents:", error);
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    if (lbaId) fetchDocuments();
  }, [lbaId]);

  const openDebtorDeletion = useCallback(() => {
    setDebtorDeletionOpen(true);
  }, []);

  const closeDebtorDeletion = useCallback(() => {
    setDebtorDeletionOpen(false);
  }, []);

  const openImagePreview = useCallback(() => {
    setImagePreviewOpen(true);
  }, []);

  const closeImagePreview = useCallback(() => {
    setImagePreviewOpen(false);
  }, []);

  const openLBAReminder = useCallback(() => {
    setLBAReminderOpen(true);
  }, []);

  const closeLBAReminder = useCallback(() => {
    setLBAReminderOpen(false);
  }, []);

  const openNavigationDropdown = useCallback(() => {
    setNavigationDropdownOpen(true);
  }, []);

  const closeNavigationDropdown = useCallback(() => {
    setNavigationDropdownOpen(false);
  }, []);

  const openLogoutConfirmation = useCallback(() => {
    setLogoutConfirmationOpen(true);
  }, []);

  const closeLogoutConfirmation = useCallback(() => {
    setLogoutConfirmationOpen(false);
  }, []);

  const navItems = [
    {
      text: "Dashboard",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/dashboard",
    },
    {
      text: "Legal proceedings & billing",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/legal_1",
    },
    {
      text: "Pre-LBA checks",
      bgColor: "bg-primary-primary-50",
      textColor: "text-neutrals-neutral-900",
      hoverBgColor: "bg-primary-primary-50",
      hoverTextColor: "text-neutrals-neutral-900",
      path: "",
    },
    {
      text: "Client-debtor interaction",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/cdi1",
    },
  ];


  return (
    <>
      <div className="w-full relative bg-neutrals-neutral-50 min-h-screen overflow-visible text-left text-sm text-neutrals-neutral-900 font-open-sans-ff-body-strong">
       
        <NavigationPanelLawyer navItems={navItems}/>
       

        <div className="w-full absolute top-[128px] flex flex-col items-start justify-start gap-6 px-4 md:px-4 lg:px-24 box-border">
          <div className="self-stretch flex flex-col items-start justify-start gap-6 text-9xl text-neutrals-neutral-900">
            <div className="self-stretch border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-row items-start justify-between pt-0 px-0 pb-6 gap-0">
              <b className="relative">Debtor details - {name || 'Unknown Debtor'}</b>
              
            </div>
           <div className="self-stretch flex flex-col items-start justify-start gap-8 text-base">
              <div className="self-stretch flex flex-col items-start justify-start gap-6">
                <div className="self-stretch flex flex-row items-start justify-start gap-6">
                  {/* Block 1 - Debtor Details */}
                  <div className="w-full h-64 shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start justify-between text-lg">
                    <div className="w-full flex flex-row items-start justify-between p-6 box-border">
                      <div className="flex flex-col items-start justify-start">
                        <div className="flex flex-col items-start justify-start gap-[24px]">
                          <div className="relative font-semibold">
                            Debtor details
                          </div>
                          <div className="flex flex-col items-start justify-start gap-[10px] text-base text-neutrals-neutral-300">
                            <div className="flex flex-row items-center justify-start gap-[10px]">
                              <img
                                className="w-5 relative h-5"
                                alt=""
                                src="assets/location.svg"
                              />
                              <div className="relative">
                                {street ? `${street}, ${city}, ${postalCode}, ${country}` : 'Address not available'}
                              </div>
                            </div>
                            <div className="flex flex-row items-center justify-start gap-[10px]">
                              <img
                                className="w-5 relative h-5"
                                alt=""
                                src="assets/mail.svg"
                              />
                              <div className="relative">{email || 'Email not available'}</div>
                            </div>
                            {invoiceNumber && (
                              <div className="flex flex-row items-start justify-start gap-[10px]">
                                <img className="w-5 relative h-5" alt="" src="assets/document.svg" />
                                <div className="relative">Invoice: {invoiceNumber}</div>
                              </div>
                            )}
                            {dueDate && (
                              <div className="flex flex-row items-start justify-start gap-[10px]">
                                <img className="w-5 relative h-5" alt="" src="assets/calendar.svg" />
                                <div className="relative">Due: {formatDate(dueDate)}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Block 2 - Amount Due */}
                  <div className="w-full h-64 shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs border-neutrals-neutral-100 border-solid border-[1px] flex flex-col items-start justify-between text-neutrals-neutral-300">
                    <div className="w-full flex flex-row items-start justify-between p-6">
                      <div className="flex flex-col items-start justify-start gap-6">
                        <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-neutrals-neutral-50 flex flex-row items-start justify-start p-2.5 border-[1px] border-solid border-neutrals-neutral-100">
                          <img
                            className="w-5 h-5"
                            alt=""
                            src="assets/error.svg"
                          />
                        </div>
                        <div className="flex flex-col items-start justify-start gap-2.5">
                          <div className="relative">Amount due</div>
                          <b className="relative text-13xl text-neutrals-neutral-900">
                            {formatCurrency(amount, currency)}
                          </b>
                        </div>
                      </div>
                      
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Attached documents */}
            <div className="self-stretch flex flex-col items-start justify-start gap-[24px] text-lg text-neutrals-neutral-900">
              <div className="w-full box-border flex flex-col items-start justify-start pt-0 px-0 pb-6 border-b-[1px] border-solid border-neutrals-neutral-100">
                <div className="self-stretch flex flex-row items-start justify-between">
                  <div className="flex flex-col items-start justify-start gap-[6px]">
                    <div className="relative font-semibold">
                      Attached documents
                    </div>
                    <div className="relative text-base text-neutrals-neutral-300">
                      List of all attached documents.
                    </div>
                  </div>
                  <CTAs
                    type="Secondarybtn"
                    text="Download all"
                    borderColor="border-neutrals-neutral-100"
                    icon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M9 11v6l2-2M9 17l-2-2"
                          stroke="#2d404e"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                        <path
                          d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5"
                          stroke="#2d404e"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                        <path
                          d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z"
                          stroke="#2d404e"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                    }
                    position="relative"
                    onClick={handleDownloadAll}
                  />
                </div>
              </div>

              {/* Document display */}
              <div className="w-full flex flex-row flex-wrap items-start justify-start gap-[24px] text-base">
                {loading ? (
                  <div>Loading documents...</div>
                ) : documents.length === 0 ? (
                  <div>No documents found</div>
                ) : (
                  documents.flatMap((doc, docIndex) =>
                    doc.files.map((file, fileIndex) => (
                      <div
                        key={`${doc.documentId}-${fileIndex}`}
                        className="w-[420px] h-[220px] flex flex-col justify-between shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-[1px] border-solid border-neutrals-neutral-100"
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between p-6">
                          <div className="flex items-center gap-3 flex-1">
                            <img
                              className="w-10 h-10"
                              alt=""
                              src={getFileIcon(
                                file.fileName.split(".").pop() || "default"
                              )}
                            />
                            <div className="flex flex-col">
                              <div className="flex items-center justify-between w-[300px]">
                                <span className="font-semibold text-neutrals-neutral-900 truncate w-[180px]">
                                  {file.fileName.length > 21
                                    ? `${file.fileName.substring(0, 21)}...`
                                    : file.fileName}
                                </span>
                                <span className="text-sm text-neutrals-neutral-300 ml-auto">
                                  {formatDate(doc.createdAt)}
                                </span>
                              </div>
                              <div className="text-sm text-neutrals-neutral-300">
                                {formatFileSize(file.fileSize)}
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Footer with Buttons */}
                        <div className="flex justify-end gap-4 p-6 border-t-[1px] border-solid border-neutrals-neutral-100">
                          <CTAs
                            type="Secondarybtn"
                            text="Preview"
                            borderColor="border-neutrals-neutral-100"
                            icon={
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M15.58 12c0 1.98-1.6 3.58-3.58 3.58S8.42 13.98 8.42 12s1.6-3.58 3.58-3.58 3.58 1.6 3.58 3.58Z"
                                  stroke="#2d404e"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                ></path>
                                <path
                                  d="M12 20.27c3.53 0 6.82-2.08 9.11-5.68.9-1.41.9-3.78 0-5.19-2.29-3.6-5.58-5.68-9.11-5.68-3.53 0-6.82 2.08-9.11 5.68-.9 1.41-.9 3.78 0 5.19 2.29 3.6 5.58 5.68 9.11 5.68Z"
                                  stroke="#2d404e"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                ></path>
                              </svg>
                            }
                            position="relative"
                            onClick={() => handlePreview(lbaId, file.index, file.fileName)}
                          />
                          <CTAs
                            type="Secondarybtn"
                            text="Download"
                            borderColor="border-neutrals-neutral-100"
                            icon={
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M9 11v6l2-2M9 17l-2-2"
                                  stroke="#2d404e"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                ></path>
                                <path
                                  d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5"
                                  stroke="#2d404e"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                ></path>
                                <path
                                  d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z"
                                  stroke="#2d404e"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                ></path>
                              </svg>
                            }
                            position="relative"
                            onClick={() => handleDownload(lbaId, file.index, file.fileName)}
                          />
                        </div>
                      </div>
                    ))
                  )
                )}
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {/* Popup components */}
      {isNavigationDropdownOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeNavigationDropdown}
        >
          <NavigationDropdown onClose={closeNavigationDropdown} />
        </PortalPopup>
      )}
      
      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
      
      {isDebtorDeletionOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeDebtorDeletion}
        >
          <DebtorDeletion onClose={closeDebtorDeletion} />
        </PortalPopup>
      )}
      {filePreview && (
        <FilePreviewModal
          fileUrl={filePreview.url}
          fileType={filePreview.type}
          onClose={() => setFilePreview(null)}
        />
      )}
    </>
  );
};

export default ClientDocuments;